import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Tab,
    tabsClasses,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { config, styles } from "../../style";
import SortIcon from "@mui/icons-material/Sort";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { recentEventsSkeleton } from "./skeleton";
import { useAuth } from "../../hooks/useAuth";
import { query } from "../../services/salesforce";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import LaunchIcon from "@mui/icons-material/Launch";
import UniversalVideoThumbnail from "../../components/UniversalVideoThumbnail";
import CloseIcon from "@mui/icons-material/Close";
import ReactPlayer from "react-player";
import { useDispatch, useSelector } from "react-redux";
import { initialize, selectDashboardData } from "./dashboardSlice";
import imageFemalePlaceholder from "../../assets/images/placeholders/image_placeholder_female.png";
import imageMalePlaceholder from "../../assets/images/placeholders/image_placeholder_male.png";

function EventsCard() {
    const dashboardData = useSelector(selectDashboardData);

    const [value, setValue] = React.useState("Recent");
    const [anchorSortEl, setAnchorSortEl] = React.useState(null);
    const sortOpen = Boolean(anchorSortEl);
    const [eventSortBy, setEventSortBy] = useState("Newest");

    const [data, setData] = useState(dashboardData.events || null);
    const [error, setError] = useState(null);
    const [status, setStatus] = useState(""); // 'idle' | 'loading' | 'succeeded' | 'failed'

    const { getUserDetails, getUserDetailsNotAsync } = useAuth();
    const userDetailsAsync = getUserDetailsNotAsync();

    const dispatch = useDispatch();

    const handleSortMenuClick = (event) => {
        setAnchorSortEl(event.currentTarget);
    };

    const handleSortMenuClose = (sortBy) => {
        if (!sortBy) {
            setAnchorSortEl(null);
            return;
        }
        setEventSortBy(sortBy);
        setAnchorSortEl(null);
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const getRecords = (type) => {
        if (data?.records?.length > 0) {
            let recordsToSort = [
                ...data?.records.filter((record) => {
                    if (type === "Upcoming") {
                        return new Date(record.startPlayDate) >= new Date();
                    } else {
                        return new Date(record.startPlayDate) < new Date();
                    }
                }),
            ];

            if (eventSortBy === "Newest") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.startPlayDate) > new Date(b.startPlayDate)
                        ? type === "Upcoming"
                            ? 1
                            : -1
                        : new Date(b.startPlayDate) > new Date(a.startPlayDate)
                        ? type === "Upcoming"
                            ? -1
                            : 1
                        : 0
                );
            } else if (eventSortBy === "Oldest") {
                recordsToSort = recordsToSort.sort((a, b) =>
                    new Date(a.startPlayDate) > new Date(b.startPlayDate)
                        ? type === "Upcoming"
                            ? -1
                            : 1
                        : new Date(b.startPlayDate) > new Date(a.startPlayDate)
                        ? type === "Upcoming"
                            ? 1
                            : -1
                        : 0
                );
            }

            if (recordsToSort.length === 0) {
                return (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "310px" }}>
                        No {type?.toLowerCase()} event found
                    </Box>
                );
            }
            return recordsToSort.map((cEvent) => <EventCard key={cEvent.id} cEvent={cEvent}></EventCard>);
        }
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "310px" }}>
                No event found
            </Box>
        );
    };

    useEffect(() => {
        const getAllEvents = async () => {
            setStatus("loading");
            const userDetails = await getUserDetails();
            try {
                const result = await query(
                    "opportunity",
                    userDetails.application === "speaker"
                        ? "getClosedChildSpeakerOpportunity"
                        : "getClosedChildOpportunityForDashboard",
                    {
                        accountId: userDetails?.contact?.accountId,
                        contactId: userDetails?.contact?.id,
                        queryFields: "getQueryFieldsListEvent",
                    }
                );
                if (result.isSuccess) {
                    const updatedData = {
                        records: result.data.map((item) => ({
                            ...item,
                            hasAccess: !!item.opportunityContactRoles?.find(
                                (i) => i.Contact.Id === userDetails?.contact?.id
                            ),
                        })),
                        userDetails: userDetails,
                    };
                    setData(updatedData);
                    setStatus("succeeded");
                    dispatch(
                        initialize({
                            events: updatedData,
                        })
                    );
                } else {
                    setError(result.errorMessage);
                    setData({ records: [], userDetails: userDetails });
                    setStatus("failed");
                }
            } catch (error) {
                setStatus("failed");
                setData({ records: [], userDetails: userDetails });
                if (error instanceof Error) {
                    setError(error.message); // This gives the actual error message
                } else {
                    setError("Something went wrong"); // Fallback in case it's not an Error object
                }
            }
        };

        if (dashboardData.events) {
            setData(dashboardData.events);
            // setStatus("succeeded");
        } else {
            // if (!data) {
            getAllEvents();
            // }
        }
    }, [value, dashboardData.events]);

    return (
        <Paper
            sx={{
                borderRadius: "10px",
                padding: "1rem",
                boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06)",
            }}>
            <Box
                display={"flex"}
                flexDirection={{
                    xs: "column",
                    sm: "row",
                }}
                justifyContent={"space-between"}
                alignItems={{ xs: "start", sm: "center" }}>
                <Typography
                    component={"h1"}
                    sx={{
                        ...styles.paperHeader,
                    }}>
                    Events at {userDetailsAsync?.contact?.accountName}
                    <Typography component={"span"} sx={{ ...styles.paperTag, marginLeft: "4px" }}>
                        ({data?.records?.length})
                    </Typography>
                </Typography>
                <Button
                    id="sort-events"
                    aria-controls={sortOpen ? "events-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={sortOpen ? "true" : undefined}
                    onClick={handleSortMenuClick}
                    startIcon={<SortIcon fontSize="small" sx={{ color: config.primaryColor }} />}
                    sx={{
                        borderRadius: "4px",
                        border: "1px solid #E9E9E9",
                        background: "#F9FAFC",
                        padding: "2px 8px",
                        marginTop: { xs: "1rem", sm: "0" },
                    }}
                    variant="outlined">
                    {`Sort by: ${eventSortBy}`}
                </Button>
                <Menu
                    id="events-menu"
                    anchorEl={anchorSortEl}
                    open={sortOpen}
                    onClose={() => handleSortMenuClose("")}
                    MenuListProps={{
                        "aria-labelledby": "sort-events",
                    }}>
                    <MenuItem onClick={() => handleSortMenuClose("Newest")} value="Newest">
                        Newest
                    </MenuItem>
                    <MenuItem onClick={() => handleSortMenuClose("Oldest")} value="Oldest">
                        Oldest
                    </MenuItem>
                </Menu>
            </Box>

            <Box
                marginTop={"0.5rem"}
                sx={{
                    "& .MuiTabPanel-root": {
                        padding: 0,
                        marginTop: "1rem",

                        maxHeight: "315px",
                        minHeight: "315px",

                        overflow: "auto",
                        overflowY: "scroll",
                        scrollbarWidth: "none", // Firefox
                        msOverflowStyle: "none", // IE/Edge
                        "&::-webkit-scrollbar": {
                            display: "none", // Chrome/Safari
                        },
                    },
                }}>
                <TabContext value={value}>
                    <TabList
                        onChange={handleChange}
                        aria-label="Event tabs"
                        sx={{
                            [`& .${tabsClasses.scrollButtons}`]: {
                                "&.Mui-disabled": { opacity: 0.3 },
                            },
                            minHeight: "unset",
                            // maxHeight: "44px",
                            "& .MuiButtonBase-root.MuiTab-root": {
                                color: "#A0A0AB",
                                fontSize: "12px",
                                fontWeight: 600,
                                textTransform: "initial",
                                minHeight: "unset",
                                padding: "10px 0px",
                                margin: "0 8px",

                                "&:first-of-type": {
                                    marginLeft: "0px",
                                },

                                "&.Mui-selected": {
                                    color: "#444CE7",
                                },
                            },

                            "& .MuiTabs-indicator": {
                                backgroundColor: "#444CE7",
                            },
                        }}>
                        <Tab label="Recent Events" value="Recent" />
                        <Tab label="Upcoming Events" value="Upcoming" />
                    </TabList>

                    <TabPanel value="Upcoming" sx={{}}>
                        {status === "loading" ? recentEventsSkeleton : <>{getRecords("Upcoming")}</>}
                    </TabPanel>
                    <TabPanel value="Recent" sx={{ padding: 0, marginTop: "1rem" }}>
                        {status === "loading" ? recentEventsSkeleton : <>{getRecords("Recent")}</>}
                    </TabPanel>
                </TabContext>
            </Box>
        </Paper>
    );
}

const EventCard = ({ cEvent }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [currentVideoURL, setCurrentVideoURL] = useState("");
    const handleCardClick = (recordId) => {
        window.open("/event-detail/" + recordId, "_blank");
    };

    const videoClick = (videoUrl) => {
        if (videoUrl) {
            setOpen(true);
            setCurrentVideoURL(videoUrl);
        }
    };
    return (
        <Card
            sx={{
                // marginBottom: "1rem",

                borderRadius: "10px",
                background: "#FFF",
                marginBottom: "1rem",

                "&:last-child": {
                    marginBottom: "0",
                },
            }}>
            <CardHeader
                title={
                    <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} flexDirection={"row"}>
                        <Box>{cEvent.eventName}</Box>
                        {cEvent?.hasAccess && (
                            <Box
                                onClick={() => handleCardClick(cEvent.id)}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    color: "#ffffff",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    marginRight: "10px",
                                }}>
                                <Tooltip title={"View Event"}>
                                    <LaunchIcon sx={{ width: "20px", height: "20px" }}></LaunchIcon>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                }
                sx={{
                    background: styles.card.cardHeaderColor,
                    padding: ".5rem 1rem",
                    color: "#fff",
                }}></CardHeader>

            <CardContent
                sx={{
                    padding: "1rem",
                    border: "1px solid #E4E4E7",
                    borderRadius: "0 0 10px 10px",
                }}>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <Box>
                            <Typography
                                component={"div"}
                                display={"flex"}
                                alignItems={"center"}
                                fontSize={"14px"}
                                fontWeight={700}
                                color={"#344054"}>
                                <Avatar
                                    alt={cEvent.speakerName}
                                    src={cEvent.speakerImageURL}
                                    sx={{ marginRight: "0.5rem" }}
                                />
                                {cEvent.speakerName}
                            </Typography>

                            {cEvent?.startPlayDate && (
                                <Box display={"flex"} flexDirection={"column"} marginTop={"1rem"}>
                                    <Typography component={"span"} color={"#A0A0AB"} fontSize={"12px"} fontWeight={600}>
                                        Date
                                    </Typography>
                                    <Typography component={"span"} color={"#26272B"} fontSize={"14px"} fontWeight={500}>
                                        <span>{format(new Date(cEvent?.startPlayDate), "MMMM d, yyyy")}</span>
                                        {cEvent?.endPlayDate && cEvent?.startPlayDate !== cEvent?.endPlayDate && (
                                            <span style={{ marginLeft: "5px" }}>
                                                {" "}
                                                - {format(new Date(cEvent?.endPlayDate), "MMMM d, yyyy")}
                                            </span>
                                        )}
                                    </Typography>
                                </Box>
                            )}
                            <Box display={"flex"} flexDirection={"column"} marginTop={"1rem"} marginRight={"0.5rem"}>
                                <Typography component={"span"} color={"#A0A0AB"} fontSize={"12px"} fontWeight={600}>
                                    Speech Topic
                                </Typography>
                                <Typography
                                    component={"span"}
                                    color={"#26272B"}
                                    fontSize={"14px"}
                                    fontWeight={500}
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitLineClamp: "2",
                                        WebkitBoxOrient: "vertical",
                                    }}>
                                    {cEvent.speechTopic || "N/A"}
                                </Typography>
                            </Box>
                            <Box display={"flex"} flexDirection={"column"} marginTop={"1rem"}>
                                <Typography component={"span"} color={"#A0A0AB"} fontSize={"12px"} fontWeight={600}>
                                    Contact
                                </Typography>
                                <Typography component={"span"} color={"#26272B"} fontSize={"14px"} fontWeight={500}>
                                    {cEvent.ownerName}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box marginTop={{ xs: "0rem", sm: "2rem" }}>
                            {cEvent?.testimonial && (
                                <Box display={"flex"} flexDirection={"column"} marginTop={"1rem"}>
                                    <Typography component={"span"} color={"#A0A0AB"} fontSize={"12px"} fontWeight={600}>
                                        Testimonial
                                    </Typography>
                                    <Box
                                        sx={{
                                            borderRadius: "10px",
                                            border: "1px solid  #EEF4FF",
                                            background: "#F5F8FF",
                                            padding: "0.5rem",
                                            marginTop: "0.5rem",
                                        }}>
                                        <Typography
                                            component={"p"}
                                            color={"#70707B"}
                                            fontSize={"10px"}
                                            fontWeight={400}
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 4,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}>
                                            {cEvent?.testimonial}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            {cEvent?.video && (
                                <Box
                                    display={"flex"}
                                    flexDirection={"column"}
                                    marginTop={"1rem"}
                                    onClick={() => videoClick(cEvent.video.URL__c)}>
                                    <Typography component={"span"} color={"#A0A0AB"} fontSize={"12px"} fontWeight={600}>
                                        Video
                                    </Typography>

                                    <Box display={"flex"} justifyContent={"start"} alignItems={"center"}>
                                        <Box sx={{ marginRight: "0.5rem", pt: "10px" }}>
                                            <UniversalVideoThumbnail
                                                videoUrl={cEvent.video.URL__c}
                                                width={50}></UniversalVideoThumbnail>
                                        </Box>
                                        <Typography
                                            component={"a"}
                                            sx={{
                                                color: "#344054",
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                textDecorationLine: "underline",
                                                textDecorationStyle: "solid",
                                                textDecorationSkipInk: "auto",
                                                textDecorationThickness: "auto",
                                                textUnderlineOffset: "auto",
                                                textUnderlinePosition: "from-font",
                                            }}>
                                            {cEvent.video.Name}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
            <VideoModal
                videoUrl={currentVideoURL}
                open={open}
                onClose={() => {
                    setOpen(false);
                    setCurrentVideoURL("");
                }}></VideoModal>
        </Card>
    );
};

const VideoModal = ({ videoUrl, open, onClose }) => {
      const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'));
      const isTabletView = useMediaQuery((theme) => theme.breakpoints.down('md'));
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: { xs: "200px", sm: "400px", md: "600px" }, // desktop gets 80% of viewport height
                    overflow: "hidden",
                    background: "unset",
                },
            }}
        >
            <DialogContent style={{ position: "relative", padding: 0, overflow: "unset" }}>
                <IconButton onClick={onClose} style={{ position: "absolute", right: 8, top: 8, zIndex: 1 }}>
                    <CloseIcon />
                </IconButton>
                <ReactPlayer
                    url={videoUrl}
                    controls
                    width="100%"
                    height={isMobileView ? "200px" : isTabletView ? "400px" : "600px"}
                    playing={true}
                    style={{ borderRadius: "10px" }}
                    config={{
                        youtube: {
                            playerVars: { showinfo: 1, rel: 0 },
                        },
                        vimeo: {
                            playerOptions: { byline: false, portrait: false, title: false },
                        },
                    }}
                />
            </DialogContent>
        </Dialog>
    );
};

export default EventsCard;
